import React from 'react';
import classNames from 'classnames';
import { RecentActivity } from '../../recentActivity';

const WarrantyIcon = ({ warrantyExpiration }) => {
  if (!warrantyExpiration) return null;
  const expirationDate = new Date(warrantyExpiration);
  const now = new Date();
  const timeDiff = expirationDate - now;
  const monthDiff = expirationDate.getMonth() - now.getMonth() + (12 * (expirationDate.getFullYear() - now.getFullYear()));
  if (timeDiff < 0) {
    return <i className="fa fa-exclamation-triangle text-danger" title="Warranty expired"></i>;
  } else if (monthDiff <= 4) {
    return <i className="fa fa-exclamation-triangle text-warning" title="Warranty expiring soon"></i>;
  } else {
    return <i className="fa fa-check text-success"></i>;
  }
}

const Status = ({ maintenanceState, powerState }) => {
  if (!maintenanceState && !powerState) return null;
  let status = "ACTIVE";
  if (powerState && powerState.toLowerCase() === "off") {
    status = "INACTIVE";
  } else if (maintenanceState && maintenanceState.toLowerCase() !== "ok") {
    status = "MAINTENANCE";
  }
  return (
    <div className="bordered-section">
      <div className={"status " + status.toLowerCase()}>
        <span>{status}</span>
      </div>
      <div className="d-flex justify-content-center smallish gap-x-4">
        {powerState && (
          <div>
            <i
              className={"fa fa-power-off mr-1 " + (powerState.toLowerCase() === "off" ? "text-danger" : "text-success")}
              title="Power state"
            ></i>
            {humanize(powerState)}
          </div>
        )}
        {maintenanceState && (
          <div>
            <i
              className={"fa fa-wrench mr-1 " + (maintenanceState.toLowerCase() === "ok" ? "text-success" : "text-warning")}
              title="Maintenance state"
            ></i>
            {humanize(maintenanceState)}
          </div>
        )}
      </div>
    </div>
  );
}

function humanize(str) {
  if (str.match(/^\s*$/)) return ""
  if (str.toLowerCase() === "ok") return "OK";
  return str
    .replace(/_/, " ")
    .replace(/^./, str[0].toUpperCase());
}

function stateClassName(metadata) {
  if (!metadata) return "";
  if (metadata.power_state && metadata.power_state.toLowerCase() === "off") {
    return "text-danger";
  } else if (metadata.maintenance_state && metadata.maintenance_state.toLowerCase() !== "ok") {
    return "text-warning";
  } else {
    return "";
  }
}

function relevantItems(items, asset) {
  let assetItems;
  if (asset.type === "Chassis") {
    assetItems = asset.assets.flatMap(a => items[`${a.type}-${a.id}`]);
  } else {
    assetItems = items[`${asset.type}-${asset.id}`] || [];
  }
  return assetItems;
}

function handleNotificationClick(notification, envopsMode) {
  if (envopsMode) {
    const category = notification.category;
    const $modal = $(".notification-category-modal").first();
    $modal.find(".notification-category-icon").first().attr("class", `notification-category-icon fa fa-lg fa-${category.icon}`);
    $modal.find(".notification-category-name").first().html(category.name);
    $modal.find(".notification-category-info").first().html(category.info);
    $modal.modal("show");
  } else {
    window.location.href = notification.show_path
  }
}

const Actions = ({asset, envopsMode}) => {
  let caseLinkParams
  if (asset.type === "Chassis") {
    caseLinkParams = asset.assets.map(a => `${asset.typeParam}s[]=${a.id}`).join("&")
  } else {
    caseLinkParams = `${asset.typeParam}s[]=${asset.id}`
  }

  if (envopsMode && asset.modalDetailsLink == null) {
    return null
  }

  let link = null
  if (asset.modalDetailsLink) {
    link = <FullDetailsModalLink asset={asset} envopsMode={envopsMode} />
  } else if (asset.link) {
    link = (
      <a
        className="btn btn-secondary btn-sm mt-2 w-100"
        href={asset.link}
      >
        Go to overview
      </a>
    )
  }

  return (
    <div className="action-buttons">
      <h6 className="mb-2">Actions</h6>
      {!envopsMode && (
        <div className="d-flex mt-2 w-100">
          <a
            className="btn btn-primary btn-sm mr-2 flex-grow-1"
            href={`cases/new?team=Operations&topic=33&${caseLinkParams}`}
            title="Report a hardware issue to our Operations team"
          >
            <i className="fa fa-users mr-2"></i>
            Report issue
          </a>
          <a
            className="btn btn-primary btn-sm flex-grow-1"
            href={`cases/new?team=Engineering&topic=32&${caseLinkParams}`}
            title="Submit a request to our Engineering team"
          >
            <i className="fa fa-engineering mr-2"></i>
            Request change
          </a>
        </div>
      )}
      {link}
    </div>
  )
}

const FullDetailsModalLink = ({asset, envopsMode}) => {
  const modal = document.querySelector("#asset-manager-full-details-modal")
  const onClick = () => {
    modal.dataset["loadContentFrom"] = asset.modalDetailsLink
    $(modal).modal("show")
  }

  return (
    <button
      className="btn btn-secondary btn-sm mt-2 w-100"
      onClick={onClick}
    >
      View details
    </button>
  )
}

const Details = ({ componentId, asset, onSelectAsset, onHoverAsset, notifications, activityURL, envopsMode, hoveredAsset }) => {
  const assetNotifications = relevantItems(notifications, asset);
  const hasAssets = asset.assets && asset.assets.length > 0;

  // TODO: consider removing metadata entirely. provide power and maintenance
  // as direct values.

  return (
    <div className={`asset-details ${hasAssets ? "has-assets" : "no-assets"}`}>
      <div className="asset-details-header">
        <h5 className="mt-1">
          <i className={"fa " + asset.icon}></i>&nbsp;
          {asset.name}
        </h5>
        <button
          className={"btn btn-link btn-sm text-muted p-0 mr-auto mb-1"}
          onClick={() => onSelectAsset(null)}
        >
          <i className="fa fa-angle-double-left mr-1"></i>
          View all assets
        </button>
        {asset.metadata && (
          <Status
            maintenanceState={asset.metadata["maintenance_state"]}
            powerState={asset.metadata["power_state"]}
          />
        )}
      </div>
      <div className="asset-details-body">
        <div className="bordered-section recent-activity-section">
          <RecentActivity
            compact
            componentId={componentId}
            asset={asset}
            activityURL={activityURL}
            envopsMode={envopsMode}
          />
        </div>
        {assetNotifications.length > 0 && (
          <div className="bordered-section system-notifications-section">
            <h6>System notifications</h6>
            <ul className="system-notifications mt-2">
              {assetNotifications.map((notification) =>
                <li
                  key={`system-notification-${notification.id}`}
                  title="View system notification"
                >
                  <div onClick={() => handleNotificationClick(notification, envopsMode)}>
                    <p className="mb-0 smallish">
                      {notification.title}
                    </p>
                    <p className="truncate text-muted mb-0 smallish">
                      {notification.associations}
                    </p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
        {hasAssets &&
          <div className="bordered-section assets-section">
            <h6>Assets</h6>
            <div className="asset-group-assets">
              {asset.assets.map((a) => (
                <div
                  key={a.id}
                  className={classNames("asset-link", {"vdc-hovered": a === hoveredAsset})}
                  onMouseEnter={onHoverAsset ? () => onHoverAsset(a) : null}
                  onMouseLeave={onHoverAsset ? () => onHoverAsset(null) : null}
                  onClick={() => {onSelectAsset(a)}}
                >
                  <i
                    className={"fa fa-cube mr-2 " + stateClassName(a.metadata)}
                    title={
                      (a.metadata.power_state ? `Power: ${humanize(a.metadata.power_state)}. ` : "") +
                      (a.metadata.maintenance_state ? `Maintenance state: ${humanize(a.metadata.maintenance_state)}.` : "")
                    }
                  ></i>
                  {a.name}
                  {relevantItems(notifications, a).length > 0 && (
                    <i className="fa fa-exclamation-triangle text-warning ml-2" title="This asset has relevant system notifications"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        }
        {asset.warrantyExpiration && (
          <div className="bordered-section warranty-section">
            <h6>Warranty expiration date</h6>
            <p className="mb-0">
              {<WarrantyIcon warrantyExpiration={asset.warrantyExpiration} />}
              &nbsp;
              {new Date(asset.warrantyExpiration).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        )}
      </div>
      <Actions asset={asset} envopsMode={envopsMode} />
    </div>
  );
};

export default Details;
